import { NamedComplexEnum } from '../../utils/complexEnum/NamedComplexEnum'
import { StatusEnumItem } from './StatusEnumItem'

export abstract class StatusEnum<
  Typename extends string,
  ItemType extends StatusEnumItem<Name, Priority, ID>,
  Name extends string,
  ID extends Lowercase<string>,
  Priority extends number
> extends NamedComplexEnum<Typename, ItemType, Name, ID> {
  /**
   * attempts to find the first value where the {@link priority} matches the
   * item's {@link StatusEnumItem.priority|priority}
   *
   * @param priority the priority to search with
   */
  fromPriority(priority: number) {
    return this.find((value) => value.priority === priority)
  }
  /**
   * finds a complex enum item by {@link StatusEnumItem.priority|priority}
   *
   * @param value the value to use when attempting to find a complex enum item
   * @see {@link fromPriority}
   */
  fromPriorityOrFail(priority: number) {
    return this.orFail(this.fromPriority(priority), priority)
  }
  /**
   * attempts to get the next {@link ItemType} by priority
   *
   * @param current an {@link ItemType} to start from
   */
  next(current: ItemType) {
    return this.find(({ priority }) => current.priority + 1 === priority)
  }
  /**
   * gets the next {@link ItemType}
   *
   * @param current an {@link ItemType} to start from
   * @see {@link next}
   */
  nextOrFail(current: ItemType) {
    return this.orFail(this.next(current), undefined)
  }
  /**
   * attempts to get the previous {@link ItemType} by priority
   *
   * @param current an {@link ItemType} to start from
   */
  previous(current: ItemType) {
    return this.find(({ priority }) => current.priority - 1 === priority)
  }
  /**
   * gets the previous {@link ItemType} by priority
   *
   * @param current an {@link ItemType} to start from
   * @see {@link previous}
   */
  previousOrFail(current: ItemType) {
    return this.orFail(this.previous(current), undefined)
  }
}
